import { Icons } from '@/components/icons';
import { ServiceAttributeIcon } from '@/components/icons/ServiceAttributeIcon';
import { TransportationTypeIcon } from '@/components/icons/TransportationTypeIcon';
import {
  AccordionContent,
  AccordionItem,
  AccordionPrimitiveRoot,
  AccordionTrigger,
} from '@/components/primitives/Accordion';
import Divider from '@/components/primitives/Divider';
import { Typography } from '@/components/primitives/Typography';
import LegIntermediateStop from '@/components/purchase/journey/LegIntermediateStop';
import LegStop from '@/components/purchase/journey/LegStop';
import { TransText } from '@/i18n/trans/text';
import type { LegDTO } from '@/types/dto/agent';
import { formatDay, formatTime } from '@/utils/dateTime';
import type { FC } from 'react';
import { useState } from 'react';

interface StopDetailsProps {
  leg: LegDTO;
}

const StopDetails: FC<StopDetailsProps> = ({ leg }) => {
  const [isAccordionOpen, setIsAccordionOpen] = useState('');
  const departureDay = formatDay(leg.departureTime);
  const arrivalDay = formatDay(leg.arrivalTime);
  const departureTime = formatTime(leg.departureTime);
  const arrivalTime = formatTime(leg.arrivalTime);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex gap-3">
        <div className="flex flex-col items-center justify-between px-1 py-0.5">
          <TransportationTypeIcon
            ptMode={leg.ptMode}
            className="text-primary"
          />
          <div className="h-full w-0.5 bg-neutral-light" />
          <Icons.destination className="text-primary" />
        </div>
        <div className="flex w-full flex-col gap-2">
          {leg.originStop && (
            <LegStop
              stop={leg.originStop}
              time={departureTime}
              day={departureDay}
            />
          )}
          {!!leg.intermediateStops?.length && (
            <AccordionPrimitiveRoot
              type="single"
              value={isAccordionOpen}
              onValueChange={setIsAccordionOpen}
              collapsible
            >
              <AccordionItem value="open" className="border-none">
                <AccordionTrigger className="relative justify-start gap-1 p-0 hover:no-underline">
                  <Typography variant="body2-bold" className="text-primary">
                    <TransText
                      i18nKey="stopsCount"
                      values={{ count: leg.intermediateStops.length }}
                    />
                  </Typography>
                  {isAccordionOpen && (
                    <div className="absolute left-[156px] grid w-32 grid-cols-2 laptop:left-32">
                      <Typography
                        variant="body2-bold"
                        className="justify-self-center"
                      >
                        <TransText i18nKey="arrival" />
                      </Typography>
                      <Typography
                        variant="body2-bold"
                        className="justify-self-center"
                      >
                        <TransText i18nKey="departure" />
                      </Typography>
                    </div>
                  )}
                </AccordionTrigger>
                <AccordionContent className="flex flex-col gap-2">
                  <Divider className="mt-1" />
                  {leg.intermediateStops.map((stop, key) => (
                    <LegIntermediateStop key={key} stop={stop} />
                  ))}
                  <Divider />
                </AccordionContent>
              </AccordionItem>
            </AccordionPrimitiveRoot>
          )}
          {leg.destinationStop && (
            <LegStop
              stop={leg.destinationStop}
              time={arrivalTime}
              day={arrivalDay}
            />
          )}
        </div>
      </div>
      <div className="flex">
        <div className="flex items-center gap-1 rounded-l bg-primary-light px-2">
          <TransportationTypeIcon ptMode={leg.ptMode} className="text-dark" />
          <Typography variant="body2-bold">{leg.serviceCode}</Typography>
        </div>
        <div className="flex w-fit gap-2 rounded-r bg-primary-pale p-1.5">
          {leg.serviceAttributes?.map((attributes, key) => (
            <ServiceAttributeIcon key={key} {...attributes} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default StopDetails;
